import { $tm } from '../..';
import { type Ref } from 'vue';
import type { TaskLoaderState } from '../loaders';
import { ModalButton } from '@/components/Modal/ModalButton';
import { randomstring } from '@/utilities/utils';
import { resolveablePromise } from '@/utilities/promiseutils';
import { shallowRef } from 'vue';
import { useDialogBuilder } from './dialogbuilder';
import type { IDialogClosecallbackParams } from './IDialogClosecallbackParams';
import type { IDialogInstance } from './IDialogInstance';
import type { IDialogOpenedCallbackParams } from './IDialogOpenedCallbackParams';
import type { OmitNamedVModelsProps, ExtractVModelKeys } from './dialogbuilder';

type DialogOptions = Partial<Pick<IDialogInstance<any, any>, 'DisableAnimation' | 'Height' | 'MinHeight' | 'Size' | 'TextAlign' | 'Type'>>;

// TODO: Modalbutton should have a generic that matches the TDialogResult, for the return type of the button action
type OpenDialogInputParameters<TComponent extends new (...args: any) => any, TDialogResult, TModelValue> = Partial<
    Pick<
        IDialogInstance<TComponent, InstanceType<TComponent>, TModelValue, TDialogResult>,
        | 'Title'
        | 'BodyComponent'
        | 'BodyString'
        | 'BodyStringParams'
        | 'TextAlign'
        | 'BodyComponentProps'
        | 'NamedVModels'
        | 'Buttons'
        | 'Size'
        | 'Type'
        | 'DisableAnimation'
        | 'EnableHideOnEscape'
        | 'VModel'
        | 'Height'
        | 'MinHeight'
        | 'UserClosedAction'
    >
>;
const dialogStack = shallowRef<Array<IDialogInstance<any, any>>>([]);

/**
 * Use the dialogmodal to show a dialog
 * @param size the size of the dialog @default sm
 * @param type The color of the dialog @default undefined
 * @param textalign Where to align the text in the dialog @default left
 * @param disableAnimation  If the dialog should be shown without animation @default false
 * @param hideOnEscape  If the dialog should be hidden when the escape key is pressed @default false
 * @returns
 */
export const useDialog = () => {
    /**
     * Shows a Yes / No dialog with the specified title and message
     *
     * @returns A promise that resolves to {@link IDialogOpenedCallbackParams} when the dialog has finished opening
     */
    const YesNo = async <
        TComponent extends new (...args: any) => any, // Component type. With any's sadly
        TProps extends OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>> =
            OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>>,
        TInstance = InstanceType<TComponent>, // The instance of the component
        TModelValue = never,
    >(input: {
        /**
         * The title of the dialog
         */
        title: string;

        /**
         * The message to display in the dialog, or a component to use as dialog body
         */
        body: string | TComponent;

        /**
         * The callback triggered when the user clicks Yes
         */
        yesCallback: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;

        /**
         * The component parameters to pass to the dialog component
         */
        componentProps?: TProps;

        /**
         * VModel for body component if applicable
         */
        vmodel?: Ref<TModelValue>;

        /**
         * Callback triggered when the user clicks No
         */
        noCallback?: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;

        /**
         * Savestate to pass to the Yes button
         */
        savestate?: TaskLoaderState;

        /**
         * Extra options to pass to the dialog
         */
        options?: DialogOptions;
    }): Promise<IDialogOpenedCallbackParams<TInstance, boolean>> => {
        const { title, body, yesCallback, componentProps, noCallback, savestate, vmodel } = input;

        const builder = useDialogBuilder().withTitle(title);

        let builder2;
        if (typeof body === 'string') {
            builder2 = builder.withBodyText(body);
        } else {
            builder2 = builder.withBodyComponent(body, { componentProps, vmodel });
        }
        return builder2
            .withNegativeButton({
                text: $tm('button.no'),
                buttonType: 'Danger',
                action: noCallback ?? (() => false),
            })
            .withButton({
                text: $tm('button.yes'),
                buttonType: 'Primary',
                action: yesCallback,
                loadstate: savestate,
            })
            .withLoadState(savestate)
            .withOptions(input.options)
            .Open();
    };

    /**
     * Shows a Select / Cancel dialog with the specified title and message
     *
     * @returns A promise that resolves to {@link IDialogOpenedCallbackParams} when the dialog has finished opening
     */
    const SelectCancel = async <
        TComponent extends new (...args: any) => any, // Component type. With any's sadly
        TProps extends OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>> =
            OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>>,
        TInstance = InstanceType<TComponent>, // The instance of the component
    >(input: {
        /**
         * The title of the dialog
         */
        title: string;

        /**
         * The message to display in the dialog, or a component to use as dialog body
         */
        body: string | TComponent;

        /**
         * The callback triggered when the user clicks Select
         * @param closepromise The callback to call when the user clicks Select
         * @returns
         */
        confirmCallback: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;

        /**
         * The parameters to pass to the dialog component
         */
        componentProps?: TProps;

        /**
         * The callback to call when the user clicks Cancel
         * @param closepromise
         * @returns
         */
        denyCallback?: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;

        /**
         * Savestate to pass to the Select button
         */
        savestate?: TaskLoaderState;

        /**
         * Extra options to pass to the dialog
         */
        options?: DialogOptions;

        /**
         * Optional action to run if the user closes the dialog by clicking escape or the corner close button
         *
         * If this is supplied, HideOnEscape will be enabled
         */
        userClosedAction?: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;
    }): Promise<IDialogOpenedCallbackParams<TInstance, boolean>> => {
        const { title, body, confirmCallback, componentProps, denyCallback, savestate } = input;

        const dialogBuilderStep1 = useDialogBuilder().withTitle(title);

        let dialogBuilderStep2;
        if (typeof body === 'string') {
            dialogBuilderStep2 = dialogBuilderStep1.withBodyText(body);
        } else {
            dialogBuilderStep2 = dialogBuilderStep1.withBodyComponent(body, { componentProps });
        }
        return dialogBuilderStep2
            .withNegativeButton({
                text: $tm('button.cancel'),
                buttonType: 'Danger',
                action: denyCallback ?? (() => false),
            })
            .withButton({
                text: $tm('button.select'),
                buttonType: 'Primary',
                action: confirmCallback,
                loadstate: savestate,
            })
            .withLoadState(savestate)
            .withOptions(input.options)
            .withHideOnEscape(input.userClosedAction)
            .Open();
    };

    /**
     * ConfirmClose dialog. Opens a dialog to confirm closing
     *
     * @returns A promise that resolves to {@link IDialogOpenedCallbackParams} when the dialog has finished opening
     */
    const ConfirmClose = async (
        input: { yesCallback?: (params: IDialogClosecallbackParams) => boolean | Promise<boolean> } = {},
    ): Promise<IDialogOpenedCallbackParams<void, boolean>> => {
        return useDialogBuilder()
            .withTitle('general.confirmclosetitle')
            .withBodyText('general.confirmclose')
            .withNegativeButton({
                text: $tm('button.no'),
                buttonType: 'Secondary',
                action: () => false,
            })
            .withButton({
                text: $tm('button.yes'),
                buttonType: 'Primary',
                action: input.yesCallback ?? (() => true),
                autofocus: true,
            })
            .withOptions({ Type: 'warning' })
            .Open();
    };

    /**
     * Show a dialog with an OK button
     *
     * @returns A promise that resolves to {@link IDialogOpenedCallbackParams} when the dialog has finished opening
     */
    const Ok = async <
        TComponent extends new (...args: any) => any, // Component type. With any's sadly
        TProps extends OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>> =
            OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>>,
        TInstance = InstanceType<TComponent>, // The instance of the component
        TModelValue = never,
    >(input: {
        /**
         * The title of the dialog
         */
        title: string;

        /**
         * The message to display in the dialog, or a component to use as dialog body
         */
        body: string | TComponent;

        /**
         * The parameters to pass to the dialog component
         */
        componentProps?: TProps;

        /**
         * VModel for body component if applicable
         */
        vmodel?: Ref<TModelValue>;

        /**
         * Callback triggered when the user clicks OK
         */
        okCallback?: (params: IDialogClosecallbackParams<TInstance>) => boolean | Promise<boolean>;

        /**
         * Extra options to pass to the dialog
         */
        options?: DialogOptions;

        /**
         * Optional action to run if the user closes the dialog by clicking escape or the corner close button
         *
         * If this is supplied, HideOnEscape will be enabled
         */
        userClosedAction?: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;
    }): Promise<IDialogOpenedCallbackParams<TInstance, boolean>> => {
        const { title, body, componentProps, okCallback, options, vmodel } = input;

        const builder = useDialogBuilder().withTitle(title);

        let builder2;
        if (typeof body === 'string') {
            builder2 = builder.withBodyText(body);
        } else {
            builder2 = builder.withBodyComponent(body, { componentProps, vmodel });
        }
        return builder2
            .withButton({
                text: $tm('button.ok'),
                buttonType: 'Primary',
                action: okCallback ?? (() => true),
            })
            .withHideOnEscape(input.userClosedAction)
            .withOptions(options)
            .Open();
    };

    /**
     * Shows a dialog with Save and Cancel buttons
     *
     * @returns A promise that resolves to {@link IDialogOpenedCallbackParams} when the dialog has finished opening
     */
    const SaveCancel = <
        TComponent extends new (...args: any) => any, // Component type. With any's sadly
        TProps extends OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>> =
            OmitNamedVModelsProps<InstanceType<TComponent>['$props'], ExtractVModelKeys<InstanceType<TComponent>['$props']>>,
        TInstance = InstanceType<TComponent>, // The instance of the component
        TModelValue = never,
    >(input: {
        /**
         * The title of the dialog
         */
        title: string;

        /**
         * The body of the dialog. This can be a component or a string
         */
        body: TComponent | string;

        /**
         * The props to pass to the component if body is a component
         */
        componentProps?: TProps;

        /**
         * VModel for body component if applicable
         */
        vmodel?: Ref<TModelValue>;

        /**
         * The callback triggered when the user clicks Save
         */
        saveCallback: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;

        /**
         * The callback triggered when the user clicks Cancel
         */
        cancelCallback?: (params: IDialogClosecallbackParams<TInstance>) => Promise<boolean>;

        /**
         * Savestate to pass to the Save button
         */
        savestate?: TaskLoaderState;

        /**
         * Extra options to pass to the dialog
         */
        options?: DialogOptions;

        /**
         * Any extra buttons to add to the modal
         *
         * Extra buttons are shown before the Save and Cancel button
         */
        extraButtons?: Array<Parameters<typeof ModalButton.Create<IDialogClosecallbackParams<TInstance>, boolean>>[0]>;
    }): Promise<IDialogOpenedCallbackParams<TInstance, boolean>> => {
        const { title, body, saveCallback, componentProps, vmodel, cancelCallback, savestate, extraButtons, options } = input;

        const builder = useDialogBuilder().withTitle(title);
        let builder2;
        if (typeof body === 'string') {
            builder2 = builder.withBodyText(body);
        } else {
            builder2 = builder.withBodyComponent(body, { componentProps, vmodel });
        }

        return builder2
            .withButtons(...(extraButtons || []))
            .withNegativeButton({
                text: $tm('button.cancel'),
                buttonType: 'Secondary',
                action: cancelCallback ?? (() => false),
            })
            .withButton({
                text: $tm('button.save'),
                buttonType: 'Primary',
                action: saveCallback,
                loadstate: savestate,
            })
            .withLoadState(savestate)
            .withOptions(options)
            .Open();
    };

    const OpenDialog = <
        TComponent extends new (...args: any) => any,
        TInstance extends InstanceType<TComponent> = InstanceType<TComponent>,
        TModelValue = any,
        TDialogResult = any,
    >(
        dialogOptions: OpenDialogInputParameters<TComponent, TDialogResult, TModelValue>,
    ): Promise<IDialogOpenedCallbackParams<TInstance, TDialogResult>> => {
        const dialogClosedPromise = resolveablePromise<TDialogResult>();

        const dialogOpenedPromise = resolveablePromise<IDialogOpenedCallbackParams<TInstance>>();

        const dialogInstance: IDialogInstance<TComponent, TInstance, TModelValue, TDialogResult> = {
            Title: dialogOptions.Title || '',
            BodyComponent: dialogOptions.BodyComponent,
            BodyString: dialogOptions.BodyString,
            BodyStringParams: dialogOptions.BodyStringParams,
            TextAlign: dialogOptions.TextAlign || 'left',
            BodyComponentProps: dialogOptions.BodyComponentProps || {},
            NamedVModels: dialogOptions.NamedVModels || {},
            Buttons: dialogOptions.Buttons || [],
            Size: dialogOptions.Size || 'sm',
            Type: dialogOptions.Type,
            DisableAnimation: dialogOptions.DisableAnimation || false,
            EnableHideOnEscape: dialogOptions.EnableHideOnEscape,
            VModel: dialogOptions.VModel,
            Height: dialogOptions.Height,
            MinHeight: dialogOptions.MinHeight,
            UserClosedAction: dialogOptions.UserClosedAction,
            UniqueLoopKey: randomstring(''),
            PromiseResolver: dialogClosedPromise.resolver,
            ClosePromise: dialogClosedPromise.promise,
            OnDialogOpened: (params) => {
                dialogOpenedPromise.resolver(params);
                return Promise.resolve();
            },
        };

        dialogStack.value = [...dialogStack.value, dialogInstance];

        return dialogOpenedPromise.promise;
    };

    return {
        /**
         * This should !!!ONLY!!! be used by the DialogSystemModalStack component to get the current dialog stack
         */
        GetDialogStack: () => dialogStack,

        /**
         * Opens a dialog with the given parameters.
         *
         * Its advised to use the dialogbuilder instead.
         */
        OpenDialog,

        /**
         * Show a dialog with Yes / No buttons
         */
        YesNo,

        /**
         * Show a dialog with Select / Cancel buttons
         */
        SelectCancel,

        /**
         * Show a dialog to confirm closing
         */
        ConfirmClose,

        /**
         * Show a dialog with an OK button
         */
        Ok,

        /**
         * Show a dialog with Save and Cancel buttons
         */
        SaveCancel,
    };
};
